import { Injectable, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { CookieService, PlatformService } from 'ngx-unificator/services';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, pairwise, tap } from 'rxjs/operators';
import { BannersService } from './banners.service';
import { FiveDollarService } from './five-dollar.service';
import { UserBonusesService } from './user/user-bonuses.service';
import { UserGroupsService } from './user/user-groups.service';
import { FIRST_DEP, ONE_PLUS_DEPOSITOR } from './user/data/user-group.data';
import { StickyBannerService } from './sticky-banner.service';
import { StickyModalConfig } from '../types/sticky-banner.type';
import { GlobalEventsService } from './global-events.service';

@Injectable({
  providedIn: 'root',
})
export class PersonalizedBannerService {
  private readonly _banner = inject(BannersService);
  private readonly _cookie = inject(CookieService);
  private readonly _fiveDollar = inject(FiveDollarService);
  private readonly _groups = inject(UserGroupsService);
  private readonly _platform = inject(PlatformService);
  private readonly _bonuses = inject(UserBonusesService);
  private readonly _stickyBanner = inject(StickyBannerService);
  private readonly _events = inject(GlobalEventsService);

  private get _personalizedBannerConfig(): StickyModalConfig {
    return this._stickyBanner.getConfigByStoreKey('personalizedBanner');
  }

  /** Starts listening to router events to trigger the personalized banner on navigation away from source routes. */
  public init(): void {
    this._events.routerNavigationEnd$
      .pipe(
        pairwise(),
        filter(([from, to]: [NavigationEnd, NavigationEnd]) => this._shouldHandleNavigation(from, to)),
        tap(([, to]: [NavigationEnd, NavigationEnd]) => this._schedulePersonalizedBannerAfterLeave(this._resolveUrl(to))),
      )
      .subscribe();
  }

  /** Fetches banner data and enqueues the personalized banner if all conditions pass. */
  private _schedulePersonalizedBannerAfterLeave(toUrl: string): void {
    const config = this._personalizedBannerConfig;

    if (!this._canSchedulePersonalizedBannerAfterLeave(toUrl, config)) {
      return;
    }

    this._resolvePersonalizedBannerData$(config)
      .pipe(tap((data) => this._stickyBanner.enqueue({ key: config.key, data })))
      .subscribe();
  }

  /** Guards scheduling: requires browser env, cookie consent, eligible user group, and no prior display cookie. */
  private _canSchedulePersonalizedBannerAfterLeave(toUrl: string, config: StickyModalConfig): boolean {
    const isEligibleUser = !this._groups.isExistGroup(ONE_PLUS_DEPOSITOR) && !this._fiveDollar.isFiveDollarCampaign;
    const isDisplayCookieSet = !!config.displayCookie && this._cookie.check(config.displayCookie);

    return this._platform.isBrowser && !this._isIgnoredRoute(toUrl, config) && !isDisplayCookieSet && isEligibleUser;
  }

  /** Final display guard: ensures banner data exists and has not already been shown (via config or data-level cookie). */
  private _canDisplayPersonalizedBanner(data: any, config: StickyModalConfig): boolean {
    const dataCookie = data?.bannerDisplayCookie;
    const isDisplayCookieSet = !!config.displayCookie && this._cookie.check(config.displayCookie);
    const isBlockedByCookie = isDisplayCookieSet || (dataCookie && this._cookie.check(dataCookie));

    return !!data && !isBlockedByCookie;
  }

  /** Combines the promo item with the active welcome bonus when both are available and the banner is displayable. */
  private _resolvePersonalizedBannerData$(config: StickyModalConfig): Observable<any> {
    return combineLatest([this._banner.item({ alias: config.key }), this._bonuses.welcomeBonusList$]).pipe(
      map(([promo, bonuses]) => [promo?.[0], bonuses.find((bonus) => bonus.active && !bonus.used)]),
      filter(([banner, bonus]) => Boolean(banner && bonus)),
      filter((data) => this._canDisplayPersonalizedBanner(data, config)),
    );
  }

  /** Returns true if the URL matches any source or ignored route defined in config. */
  private _isIgnoredRoute(url: string, config: StickyModalConfig): boolean {
    return [...(config.sourceRoutes ?? []), ...(config.ignoreRoutes ?? [])].some((route) => url?.includes(route));
  }

  /** Only handle navigation when leaving a source route and not landing on an ignored route. */
  private _shouldHandleNavigation(from: NavigationEnd, to: NavigationEnd): boolean {
    const config = this._personalizedBannerConfig;

    return this._isSourceRoute(this._resolveUrl(from), config) && !this._isIgnoredRoute(this._resolveUrl(to), config);
  }

  /** Returns true if the URL matches one of the configured source routes. */
  private _isSourceRoute(url: string, config: StickyModalConfig): boolean {
    return !!config.sourceRoutes?.some((route) => url?.includes(route));
  }

  /** Prefers urlAfterRedirects to handle redirect chains correctly. */
  private _resolveUrl(event: NavigationEnd): string {
    return event.urlAfterRedirects || event.url;
  }
}
